// Edit a webhook's name + source. Mode is fixed at creation time
// (changing it would invalidate existing data). Secret rotation is a
// separate action with its own confirmation.

import { Dialog } from "@base-ui-components/react/dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RotateCw, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Pill } from "@/components/glass";

interface EditProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	projectId: string;
	webhookId: string;
}

interface Webhook {
	id: string;
	name: string;
	sourceName: string;
	enabled: boolean;
	mode: string;
}

async function fetchOne(
	projectId: string,
	webhookId: string,
): Promise<Webhook | null> {
	const r = await fetch(`/api/projects/${projectId}/webhooks`, {
		credentials: "include",
	});
	if (!r.ok) return null;
	const body = (await r.json()) as { webhooks: Webhook[] };
	return body.webhooks.find((w) => w.id === webhookId) ?? null;
}

export function WebhookEditDialog({ open, onOpenChange, projectId, webhookId }: EditProps) {
	const qc = useQueryClient();
	const whQ = useQuery({
		queryKey: ["webhook", projectId, webhookId],
		queryFn: () => fetchOne(projectId, webhookId),
		enabled: open,
	});

	const [name, setName] = useState("");
	const [sourceName, setSourceName] = useState("");
	const [newSecret, setNewSecret] = useState<string | null>(null);

	useEffect(() => {
		if (whQ.data) {
			setName(whQ.data.name);
			setSourceName(whQ.data.sourceName);
		}
	}, [whQ.data]);

	const save = useMutation({
		mutationFn: async () => {
			const r = await fetch(`/api/projects/${projectId}/webhooks/${webhookId}`, {
				method: "PATCH",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ name: name.trim(), sourceName: sourceName.trim() }),
			});
			if (!r.ok) throw new Error(`save: ${r.status}`);
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["webhooks", projectId] });
			onOpenChange(false);
		},
	});

	const rotate = useMutation({
		mutationFn: async () => {
			const r = await fetch(
				`/api/projects/${projectId}/webhooks/${webhookId}/rotate-secret`,
				{ method: "POST", credentials: "include" },
			);
			if (!r.ok) throw new Error(`rotate: ${r.status}`);
			const body = (await r.json()) as { secret: string };
			return body.secret;
		},
		onSuccess: (secret) => {
			setNewSecret(secret);
			qc.invalidateQueries({ queryKey: ["webhooks", projectId] });
		},
	});

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
				<Dialog.Popup
					className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[480px] max-w-[calc(100vw-32px)] rounded-[var(--radius-panel)] p-5"
					style={{
						background: "rgb(20 20 23 / 0.98)",
						backdropFilter: "blur(20px) saturate(180%)",
						WebkitBackdropFilter: "blur(20px) saturate(180%)",
						border: "1px solid rgb(255 255 255 / 0.1)",
						boxShadow: "0 24px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)",
					}}
				>
					<div className="flex items-center justify-between mb-4">
						<Dialog.Title className="text-[14px] font-medium">Edit webhook</Dialog.Title>
						<Dialog.Close className="text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors">
							<X className="w-4 h-4" />
						</Dialog.Close>
					</div>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							save.mutate();
						}}
						className="space-y-3"
					>
						<Field label="Name">
							<input
								type="text"
								value={name}
								onChange={(e) => setName(e.target.value)}
								className="w-full h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[13px] outline-none focus:border-[var(--color-border-hi)]"
							/>
						</Field>
						<Field label="Source name">
							<input
								type="text"
								value={sourceName}
								onChange={(e) => setSourceName(e.target.value)}
								className="w-full h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] mono text-[13px] outline-none focus:border-[var(--color-border-hi)]"
							/>
						</Field>
						<div>
							<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-1">
								Signing secret
							</div>
							{newSecret ? (
								<div className="space-y-1">
									<div className="rounded-md bg-black/30 border border-[var(--color-border)] p-2 mono text-[12px] break-all">
										{newSecret}
									</div>
									<div className="text-[11px] text-[var(--color-text-dim)]">
										Save it now. We won't show this again.
									</div>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<div className="flex-1 px-3 py-1.5 rounded-md bg-black/30 border border-[var(--color-border)] mono text-[12px] text-[var(--color-text-muted)]">
										••••••••••••••••••
									</div>
									<Pill
										type="button"
										onClick={() => rotate.mutate()}
										disabled={rotate.isPending}
									>
										{rotate.isPending ? (
											<Loader2 className="w-3.5 h-3.5 animate-spin" />
										) : (
											<RotateCw className="w-3.5 h-3.5" />
										)}
										Rotate
									</Pill>
								</div>
							)}
						</div>
						{save.isError && (
							<div className="text-[12px] text-[var(--color-danger)]">
								{(save.error as Error).message}
							</div>
						)}
						<div className="flex justify-end gap-2 pt-1">
							<Dialog.Close render={<Pill>Close</Pill>} />
							<Pill type="submit" disabled={save.isPending}>
								{save.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
								Save
							</Pill>
						</div>
					</form>
				</Dialog.Popup>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div>
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-1">
				{label}
			</div>
			{children}
		</div>
	);
}
